import { z } from "zod"
import { listingStatusSchema, type ListingRun } from "@nova-ai/shared"
import { humanizeMarketplaceId } from "./marketplace"

const RUN_READINESS = {
  ready: "ready",
  fix: "fix",
  offers: "offers",
  unchecked: "unchecked",
} as const
export const runsSearchSchema = z.object({
  status: listingStatusSchema.or(z.literal("all")).default("open"),
  marketplace: z.string().optional(),
  startedBy: z.string().optional(),
  origin: z.enum(["operator", "automatic"]).optional(),
  archived: z.literal("true").optional().catch(undefined),
  ready: z.enum(RUN_READINESS).optional().catch(undefined),
})
export type RunsSearch = z.infer<typeof runsSearchSchema>
export type ReadinessFilter = NonNullable<RunsSearch["ready"]>
type ReadinessRun = Pick<ListingRun, "nokFixable" | "nokBlocked">
type SummaryRun = ReadinessRun &
  Pick<ListingRun, "marketplace" | "offersNeededCount" | "feedRowCount">
export function deriveRunReadiness(run: ReadinessRun) {
  if (run.nokFixable === null || run.nokBlocked === null) {
    return {
      key: RUN_READINESS.unchecked,
      label: "Not checked yet",
      note: "Checks run after import prep",
      tone: "muted",
    } as const
  }
  if (run.nokFixable === 0 && run.nokBlocked === 0) {
    return {
      key: RUN_READINESS.ready,
      label: "Ready to export",
      note: "No gaps",
      tone: "success",
    } as const
  }
  return {
    key: RUN_READINESS.fix,
    label:
      run.nokFixable > 0
        ? `${formatRunNumber(run.nokFixable)} to fix`
        : `${formatRunNumber(run.nokBlocked)} blocked`,
    note:
      run.nokBlocked > 0
        ? `${formatRunNumber(run.nokBlocked)} blocked / excluded`
        : "Fixable in the run",
    tone: "warning",
  } as const
}
export const formatRunNumber = (count: number) => count.toLocaleString("en")
export function deriveReadinessTiles(runs: readonly SummaryRun[]) {
  const ready = runs.filter((run) => deriveRunReadiness(run).key === RUN_READINESS.ready)
  const fixes = runs.filter((run) => deriveRunReadiness(run).key === RUN_READINESS.fix)
  const offersUnknown = runs.length > 0 && runs.every((run) => run.offersNeededCount === null)
  const offers = runs.filter((run) => (run.offersNeededCount ?? 0) > 0)
  const gaps = fixes.reduce((total, run) => total + (run.nokFixable ?? 0), 0)
  const blocked = fixes.reduce((total, run) => total + (run.nokBlocked ?? 0), 0)
  return [
    {
      key: RUN_READINESS.ready,
      label: "Ready to export",
      value: ready.length,
      note: readyTileNote(ready),
      tone: "success",
    },
    {
      key: RUN_READINESS.fix,
      label: "Needs fixes",
      value: fixes.length,
      note:
        gaps === 0 && blocked > 0
          ? `${formatRunNumber(blocked)} blocked / excluded`
          : `${formatRunNumber(gaps)} gaps across runs`,
      tone: "warning",
    },
    {
      key: RUN_READINESS.offers,
      label: "Offers needed",
      value: offersUnknown
        ? null
        : offers.reduce((total, run) => total + (run.offersNeededCount ?? 0), 0),
      note: offersUnknown
        ? "Not checked yet"
        : `in ${offers.length} ${offers.length === 1 ? "run" : "runs"}`,
      tone: "warning",
    },
    {
      key: RUN_READINESS.unchecked,
      label: "Not checked yet",
      value: runs.filter((run) => deriveRunReadiness(run).key === RUN_READINESS.unchecked).length,
      note: "Waiting for gap check",
      tone: "muted",
    },
  ]
}
export function filterReadyRuns<Run extends SummaryRun>(
  runs: readonly Run[],
  filter: ReadinessFilter | undefined
): readonly Run[] {
  if (!filter) {
    return runs
  }
  return runs.filter((run) =>
    filter === RUN_READINESS.offers
      ? (run.offersNeededCount ?? 0) > 0
      : deriveRunReadiness(run).key === filter
  )
}
export function runFeedTotals(runs: readonly Pick<ListingRun, "feedRowCount">[]) {
  return {
    rows: runs.reduce((total, run) => total + (run.feedRowCount ?? 0), 0),
    built: runs.filter((run) => run.feedRowCount !== null).length,
    total: runs.length,
  }
}
export function clearRunsFilters(search: RunsSearch): RunsSearch {
  return { status: search.status }
}
export function hasRunsFilters(search: RunsSearch): boolean {
  return Boolean(
    search.marketplace ?? search.startedBy ?? search.origin ?? search.archived ?? search.ready
  )
}

function readyTileNote(runs: readonly SummaryRun[]): string {
  if (runs.length === 1 && runs[0]) {
    return humanizeMarketplaceId(runs[0].marketplace)
  }
  return runs.length === 0 ? "None yet" : "runs"
}

export function runSourceLabel(
  run: Pick<ListingRun, "origin" | "sourceType" | "sourceValue">
): string {
  if (run.origin === "automatic") {
    return "Tracked changes"
  }
  if (run.sourceType === "master-category") {
    return `Category: ${run.sourceValue}`
  }
  if (run.sourceType === "magento-category") {
    return `Magento category: ${run.sourceValue}`
  }
  if (run.sourceType === "skus" || run.sourceType === "sku-list") {
    const count = run.sourceValue.split(",").filter((sku) => sku.trim().length > 0).length
    return `${count}+ SKUs`
  }
  return `${run.sourceType}: ${run.sourceValue}`
}
